import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { formatDateTime } from "@/i18n/format";
import { CONVERSATION_LIST_POLL_MS, THREAD_POLL_MS } from "@/lib/chatQueries";
import type { ModeratorConversation, ModeratorMessage } from "@/services/moderators/generated/model";
import {
  getModeratorsConversationsListQueryKey,
  useModeratorsConversationsList,
  useModeratorsConversationsMarkReadCreate,
  useModeratorsConversationsMessagesRetrieve,
  useModeratorsConversationsSendCreate,
} from "@/services/moderators/generated/moderators-conversations/moderators-conversations";
import "./ModeratorPage.css";
import "./ModeratorConversationsPage.css";

const ModeratorConversationsPage = () => (
  <ModeratorLayout>
    <ConversationsContent />
  </ModeratorLayout>
);

const ConversationsContent = () => {
  const { t } = useTranslation(["deals", "common"]);
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [composer, setComposer] = useState("");

  const queryParams = search.trim() ? { search: search.trim() } : undefined;
  // No push channel: poll so company replies show up (see lib/chatQueries).
  const { data, isLoading, error } = useModeratorsConversationsList(queryParams, {
    query: { refetchInterval: CONVERSATION_LIST_POLL_MS },
  });
  const conversations = unwrapPaginated<ModeratorConversation>(data);

  const { data: messagesResp, isLoading: messagesLoading } = useModeratorsConversationsMessagesRetrieve(
    selectedId ?? 0,
    undefined,
    { query: { enabled: selectedId != null, refetchInterval: THREAD_POLL_MS } },
  );
  const messages = unwrapPaginated<ModeratorMessage>(messagesResp);

  const markReadMutation = useModeratorsConversationsMarkReadCreate({
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getModeratorsConversationsListQueryKey(queryParams) }),
    },
  });

  const sendMutation = useModeratorsConversationsSendCreate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getModeratorsConversationsListQueryKey(queryParams) });
        if (selectedId != null) {
          queryClient.invalidateQueries({
            predicate: (q) =>
              Array.isArray(q.queryKey) && q.queryKey[0] === `/api/v1/moderators/conversations/${selectedId}/messages/`, // i18n-ignore (query key)
          });
        }
        setComposer("");
      },
    },
  });

  // When a conversation is opened, mark it read.
  useEffect(() => {
    if (selectedId != null) {
      markReadMutation.mutate({ id: selectedId });
    }
    // markReadMutation is stable from React Query; intentional dep on id only.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  }, [selectedId, markReadMutation.mutate]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!composer.trim() || selectedId == null) return;
    sendMutation.mutate({ id: selectedId, data: { content: composer.trim() } });
  };

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("conversations.title")}</h1>
          <p className="mod-page__subtitle">{t("conversations.subtitle")}</p>
        </div>
      </header>

      <div className="mod-conversations__layout">
        <aside className="mod-conversations__sidebar">
          <form
            className="mod-page__toolbar"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchInput);
            }}
          >
            <input
              type="search"
              className="mod-page__input"
              placeholder={t("conversations.searchPlaceholder")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="mod-page__btn mod-page__btn--primary">
              {t("common:actions.apply")}
            </button>
          </form>

          {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
          {error ? <div className="mod-page__error">{t("conversations.loadError")}</div> : null}
          {!isLoading && conversations.length === 0 ? (
            <div className="mod-page__empty">{t("conversations.empty")}</div>
          ) : null}

          <ul className="mod-conversations__list">
            {conversations.map((conv) => (
              <li key={conv.id}>
                <button
                  type="button"
                  className={`mod-conversations__list-item ${
                    selectedId === conv.id ? "mod-conversations__list-item--active" : ""
                  }`}
                  onClick={() => setSelectedId(conv.id)}
                >
                  <div className="mod-conversations__list-item-name">
                    {conv.company?.companyName ?? t("conversations.fallbackName", { id: conv.id })}
                  </div>
                  <div className="mod-conversations__list-item-meta">
                    {conv.lastMessage?.content
                      ? truncate(conv.lastMessage.content, 60)
                      : t("conversations.noMessagesYet")}
                  </div>
                  {conv.unreadCount > 0 ? <span className="mod-conversations__badge">{conv.unreadCount}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="mod-conversations__panel">
          {selectedId == null ? (
            <div className="mod-page__empty">{t("conversations.selectPrompt")}</div>
          ) : (
            <>
              <div className="mod-conversations__messages">
                {messagesLoading ? <div className="mod-page__loading">{t("conversations.loadingMessages")}</div> : null}
                {messages.length === 0 && !messagesLoading ? (
                  <div className="mod-page__empty">{t("conversations.noMessages")}</div>
                ) : null}
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`mod-conversations__message ${
                      m.fromModerator ? "mod-conversations__message--out" : "mod-conversations__message--in"
                    }`}
                  >
                    <div className="mod-conversations__message-meta">
                      {m.fromModerator
                        ? (m.moderatorName ?? t("conversations.you"))
                        : (m.senderUserName ?? t("conversations.company"))}{" "}
                      · {formatDateTime(m.createdAt)}
                    </div>
                    <div className="mod-conversations__message-body">{m.content}</div>
                  </div>
                ))}
              </div>

              <form className="mod-conversations__composer" onSubmit={handleSend}>
                <textarea
                  className="mod-page__textarea"
                  placeholder={t("conversations.replyPlaceholder")}
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                />
                <button
                  type="submit"
                  className="mod-page__btn mod-page__btn--primary"
                  disabled={!composer.trim() || sendMutation.isPending}
                >
                  {sendMutation.isPending ? t("common:actions.sending") : t("common:actions.send")}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

function truncate(value: string, n: number): string {
  return value.length > n ? `${value.slice(0, n - 1)}…` : value;
}

export default ModeratorConversationsPage;
